import { parseDate } from "../components/DatePicker";
import type { SearchFilters } from "./MemberResultsTable";

/**
 * The member-search submit rule, shared by `MemberSearchPage` and Home's
 * `MemberSearchWidget` so the two cannot drift apart.
 *
 * Everyone: a name PAIR of at least two characters each, or a Member ID on its
 * own. `buildFilter` drops a lone first or last name, so a half-filled name
 * would silently search on nothing at all.
 *
 * Provider Portal logins (`requiresDob`) additionally need a Date of Birth with
 * a name search — the same rule providerPortalFrontend's own member search
 * enforces (`components/MemberSearch/schema.tsx`, AM-5482: name-only lookups
 * are not permitted). PP's `searchProviderPortalMembers` returns nothing for a
 * name-only search, so letting one through would only ever show "No members
 * found". The DOB must be a COMPLETE, real date: DatePicker emits partial
 * masked values while the user types ("01/01/19"), and `buildIsoDate` drops
 * anything that is not full MM/DD/YYYY — so a partial DOB would reach PP as a
 * name-only search. A Member ID alone is still a valid search, as it is in PP — but, as
 * in PP, it must be at least 5 characters.
 *
 * Returns the message to show, or null when the search may run.
 */
export function validateMemberSearch(
  filters: SearchFilters,
  { requiresDob }: { requiresDob: boolean },
): string | null {
  const hasNamePair =
    filters.firstName.trim().length >= 2 && filters.lastName.trim().length >= 2;
  const mpi = filters.mpi.trim();
  const hasDob = parseDate(filters.dob.trim()) !== null;

  if (mpi) {
    return requiresDob && mpi.length < 5
      ? "Member ID must be at least 5 characters long."
      : null;
  }
  if (!hasNamePair) {
    return requiresDob
      ? "Enter both first and last name (at least 2 characters each) with a Date of Birth, or a Member ID."
      : "Enter both first and last name (at least 2 characters each), or a Member ID.";
  }
  if (requiresDob && !hasDob) {
    return filters.dob.trim()
      ? "Enter a complete Date of Birth (MM/DD/YYYY)."
      : "Date of Birth is required when searching by name.";
  }
  return null;
}
